
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, CheckCircle, Tag, Package, RefreshCw } from "lucide-react";
import { useMembershipPurchase } from "@/hooks/useMembershipPurchase";
import { useAvailableMembershipCoupons } from "@/hooks/useMembershipCoupons";
import { useAuth } from "@/hooks/useAuth";
import MembershipCouponSelector from "@/components/membership/MembershipCouponSelector";

interface MembershipCardProps {
  membership: {
    id: string;
    title: string;
    sessionCount: number;
    price: number;
    type: string;
    services?: Array<{ id: string; name: string; duration_minutes?: number; }>;
    is_subscription?: boolean;
  };
  providerId?: string;
}

const MembershipCard = ({ membership, providerId }: MembershipCardProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState<string>();
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const membershipPurchase = useMembershipPurchase();
  const { data: availableCoupons } = useAvailableMembershipCoupons(providerId);

  const finalPrice = membership.price - appliedDiscount;
  const hasCoupons = availableCoupons && availableCoupons.length > 0;

  // Determine if this is a membership or package based on type
  const isMembership = membership.type.toLowerCase() === 'unlimited' || membership.type.toLowerCase() === 'sessions_per_month';
  
  // Fix: Use the actual is_subscription field from the membership data
  const isSubscription = membership.is_subscription === true;

  // Determine if this is a pack
  const isPack = membership.type.toLowerCase() === 'pack';

  const handleCouponSelect = (userCouponId: string, discountAmount: number) => {
    setSelectedCouponId(userCouponId);
    setAppliedDiscount(discountAmount);
  };

  const handleCouponRemove = () => {
    setSelectedCouponId(undefined);
    setAppliedDiscount(0);
  };

  const handlePurchaseClick = () => {
    // Check if user is authenticated
    if (!user) {
      // Redirect to auth page if not logged in
      navigate("/auth");
      return;
    }

    // Proceed with purchase if authenticated
    handlePurchase();
  };

  const handlePurchase = () => {
    // Create the payment with coupon information if selected
    const purchaseData = {
      membershipId: membership.id,
      successUrl: `${window.location.origin}/membership-success`,
      cancelUrl: window.location.href,
      userCouponId: selectedCouponId,
      discountAmount: appliedDiscount
    };

    membershipPurchase.mutate(purchaseData);
  };

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pack':
        return 'bg-blue-100 text-blue-800';
      case 'monthly':
        return 'bg-green-100 text-green-800';
      case 'unlimited':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatServiceWithDuration = (service: { name: string; duration_minutes?: number }) => {
    if (service.duration_minutes) {
      return `${service.name} (${service.duration_minutes} min)`;
    }
    return service.name;
  };

  const getButtonText = () => {
    return isPack ? "Purchase Package" : "Purchase Membership";
  };

  return (
    <Card className="h-full flex flex-col hover:shadow-lg transition-shadow">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between mb-2">
          <CardTitle className="text-lg">{membership.title}</CardTitle>
          <div className="flex gap-2">
            {isSubscription ? (
              <Badge className="bg-green-100 text-green-800">
                <RefreshCw className="h-3 w-3 mr-1" />
                Subscription
              </Badge>
            ) : (
              <Badge className={getTypeColor(membership.type)}>
                {membership.type}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col">
        <div className="space-y-3 mb-6">
          <div className="flex items-center space-x-2 text-gray-600">
            <Calendar className="h-4 w-4" />
            <span className="text-sm">
              {membership.sessionCount > 0 
                ? `${membership.sessionCount} sessions` 
                : 'Unlimited sessions'}
            </span>
          </div>
          
          {/* Show included services if available */}
          {membership.services && membership.services.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-gray-600">
                <Package className="h-4 w-4" />
                <span className="text-sm font-medium">Included Services:</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {membership.services.map((service) => (
                  <Badge key={service.id} variant="secondary" className="text-xs">
                    {formatServiceWithDuration(service)}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {hasCoupons && (
            <div className="flex items-center space-x-2 text-green-600">
              <Tag className="h-4 w-4" />
              <span className="text-sm">Coupons available</span>
            </div>
          )}
        </div>

        <div className="mt-auto">
          <div className="text-center mb-4">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-3xl font-bold">£{membership.price.toFixed(2)}</span>
              {isSubscription && (
                <span className="text-sm text-gray-500">a month</span>
              )}
            </div>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="w-full bg-black text-white hover:bg-gray-800"
              >
                {getButtonText()}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Purchase Summary</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Membership summary */}
                <Card>
                  <CardContent className="pt-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Item:</span>
                        <span className="font-medium">{membership.title}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sessions:</span>
                        <span>{membership.sessionCount > 0 ? membership.sessionCount : 'Unlimited'}</span>
                      </div>
                      {membership.services && membership.services.length > 0 && (
                        <div className="flex justify-between items-start">
                          <span>Services:</span>
                          <div className="text-right text-sm">
                            {membership.services.map((service) => (
                              <div key={service.id}>{formatServiceWithDuration(service)}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Coupon selector */}
                {providerId && (
                  <MembershipCouponSelector
                    providerId={providerId}
                    membershipPrice={membership.price}
                    onCouponSelect={handleCouponSelect}
                    onCouponRemove={handleCouponRemove}
                    selectedCouponId={selectedCouponId}
                    appliedDiscount={appliedDiscount}
                  />
                )}

                {/* Purchase button */}
                <Button
                  onClick={handlePurchaseClick}
                  disabled={membershipPurchase.isPending}
                  className="w-full bg-black text-white hover:bg-gray-800"
                >
                  {membershipPurchase.isPending ? (
                    "Processing..."
                  ) : (
                    `Purchase for £${finalPrice.toFixed(2)}`
                  )}
                </Button>

                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4" />
                  <span>Secure payment with Stripe</span>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
};

export default MembershipCard;
